import { apiClient } from '../lib/apiClient';

/**
 * Register a new customer
 * @param {Object} customerData - Customer registration data
 * @param {string} customerData.firstName - Customer first name
 * @param {string} customerData.lastName - Customer last name
 * @param {string} customerData.company - Company name
 * @param {number} customerData.roleId - Role ID
 * @param {string} customerData.userName - Username
 * @param {number} customerData.customerAccount - Customer account number
 * @param {string} customerData.password - Password
 * @param {string} customerData.nationalIdentity - National identity number
 * @returns {Promise<Object>} Response data
 */
export const saveCustomer = async (customerData) => {
  try {
    const response = await apiClient.post('/usermgmt/saveCustomer', {
      firstName: customerData.firstName,
      lastName: customerData.lastName,
      company: customerData.company,
      roleId: customerData.roleId || 0,
      userName: customerData.userName,
      customerAccount: customerData.customerAccount || 0,
      password: customerData.password,
      nationalIdentity: customerData.nationalIdentity || '',
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Customer login
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.userName - Username
 * @param {string} credentials.password - Password
 * @returns {Promise<Object>} Response data with token and user info
 */
export const customerLogin = async (credentials) => {
  try {
    const response = await apiClient.post('/usermgmt/customerLogin', {
      userName: credentials.userName,
      password: credentials.password,
    });
    
    // Handle response structure: { success, statusCode, message, data }
    // where data = { access_token, role, customer, customerAccount }
    const responseData = response.data;
    
    if (!responseData.success) {
      throw new Error(responseData.message || 'Login failed');
    }

    const loginData = responseData.data || responseData;
    const token = loginData.access_token || loginData.token;
    
    if (!token) {
      throw new Error('No token received from server');
    }

    // Decode JWT to get user info (simple decode, not verification)
    let userId = null;
    let userName = credentials.userName;
    let roleId = null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.id;
      userName = payload.userName || userName;
      roleId = payload.roleId;
    } catch (e) {
      console.warn('Could not decode token:', e);
    }

    // Extract customer and customerAccount from response
    const customer = loginData.customer || null;
    const customerAccount = loginData.customerAccount || null;

    // Build user object with customer and customerAccount details
    const user = {
      userId: customer?.userId || userId,
      id: customer?.userId || userId,
      userName: customer?.userName || userName,
      username: customer?.userName || userName,
      roleId: customer?.roleId || roleId,
      role: loginData.role,
      email: customer?.email || '',
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      company: customer?.company || '',
      customerId: customer?.customerId || null,
      customerAccountId: customer?.customerAccountId || null,
      customerAccount: customerAccount,
      
      // Include all customer account details if available
      ...(customerAccount && {
        customerAccountName: customerAccount.customerAccountName,
        customerAccountDesc: customerAccount.custAccountDesc,
        priceConfigId: customerAccount.priceConfigId,
        pricingProfileName: customerAccount.pricingProfileName,
        customerViewId: customerAccount.customerViewId,
        customerViewName: customerAccount.customerViewName,
      }),
    };

    return {
      token: token,
      access_token: token,
      user: user,
      customer: customer,
      customerAccount: customerAccount,
      role: loginData.role,
      success: true,
    };
  } catch (error) {
    // Handle error response structure
    if (error.response?.data) {
      const errorData = error.response.data;
      if (errorData.message) {
        throw new Error(errorData.message);
      }
      if (errorData.data?.message) {
        throw new Error(errorData.data.message);
      }
    }
    throw error;
  }
};

/**
 * Fetch current user details
 * @returns {Promise<Object>} User details
 */
export const getCurrentUserDetails = async () => {
  try {
    const response = await apiClient.get('/usermgmt/getByCustomerByUserId');
    if (response.data && response.data.success) {
      return response.data.data || response.data;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error;
  }
};

export default {
  saveCustomer,
  customerLogin,
  getCurrentUserDetails,
};

